import * as yup from 'yup';

import { useKradInputStore } from '@/features/Sparten/krad/useInputStore';
import {
  hasHsnTsn,
  hasZeitwert,
  hasLeergewicht,
  hasKradBauart,
  hasKilometerstand,
  isLeichtkraftRadOrRoller,
} from '@/features/Sparten/shared/renderConditions/kfz/sharedRenderConditions';
import { assistenzsysteme } from '@/features/Sparten/shared/schema/kfz/assistenzSystemeSchema/assistenzSystemeSchema';

export const validateZulassungsdatumAufVN = (value: Date | null): boolean => {
  const store = useKradInputStore();

  if (value == null || store.fahrzeug.erstbesitzer) {
    return true;
  }

  const zulassung = store.fahrzeug.erstzulassung;

  return !(zulassung !== null && value < zulassung);
};

export const minimumErstzulassungsdatum = new Date('1900-01-01');
export const validateErstzulassungsdatum = (value: Date | null): boolean =>
  value === null || value >= minimumErstzulassungsdatum;

export const fahrzeugSchema = yup.object({
  assistenzsysteme,
  hsn: yup
    .string()
    .default('')
    .when({
      is: () => hasHsnTsn.value,
      then: yup.string().required(),
    }),
  tsn: yup
    .string()
    .default('')
    .when({
      is: () => hasHsnTsn.value,
      then: yup.string().required(),
    }),
  hubraum: yup
    .number()
    .default(0)
    .min(1)
    .max(10000)
    .required()
    .when({
      is: () => isLeichtkraftRadOrRoller.value,
      then: yup.number().min(50).max(125).required(),
    }),
  leistung: yup
    .number()
    .default(0)
    .min(1, 'Die Leistung in kW muss mindestens 1 betragen')
    .required()
    .when({
      is: () => isLeichtkraftRadOrRoller.value,
      then: yup
        .number()
        .max(11, 'Die Leistung in kW darf höchstens 11 betragen')
        .required(),
    }),
  neuwert: yup.number().default(0).min(1).max(100000).required(),
  zeitwert: yup
    .number()
    .default(0)
    .when({
      is: () => hasZeitwert.value,
      then: yup.number().min(1).max(100000).required(),
    }),
  hoechstgeschwindigkeit: yup.number().default(0).min(1).max(400).required(),
  leergewicht: yup
    .number()
    .default(0)
    .when({
      is: () => hasLeergewicht.value,
      then: yup.number().min(1).max(1000).required(),
    }),
  kradBauart: yup
    .string()
    .default('')
    .when({
      is: () => hasKradBauart.value,
      then: yup.string().required(),
    }),
  fahrleistung: yup.number().default(0).min(100).required(),
  kilometerstand: yup
    .number()
    .default(null)
    .nullable()
    .when({
      is: () => hasKilometerstand.value,
      then: yup.number().min(1).required(),
    }),
  finanzierungsart: yup.string().default('').required(),
  stellplatz: yup.string().default('').required(),
  auslandsnutzung: yup.string().default(''),
  nutzungsart: yup.string().default('').required(),
  erstzulassung: yup
    .date()
    .nullable()
    .default(null)
    .required()
    .test(
      'validErstzulassungsDatum',
      'Darf nicht vor 01.01.1900 liegen',
      validateErstzulassungsdatum,
    ),
  erstbesitzer: yup.boolean().default(false),
  erstmaligeZulassungAufVN: yup
    .date()
    .nullable()
    .default(null)
    .test(
      'validZulassungsDatum',
      'Darf nicht vor Erstzulassung liegen.',
      validateZulassungsdatumAufVN,
    )
    .when('erstbesitzer', {
      is: (val: boolean) => !val,
      then: yup.date().required(),
    }),
});
